import React from "react";
import { Switch } from "react-router-dom";
import Header from "../../components/common/PL/Header/Header";
import Sidebar from "../../components/common/PL/Sidebar/Sidebar";
import { useStore } from "../../hooks/useStore";
import ProtectedRoutes from "../../routes/ProtectedRoutes";
import { RoutePaths } from "../../utils/Items";
import Home from "./Home/Home";
import ExitUsers from "./ExitUsers/ExitUsers";
import RegistroIngreso from "./RegistroIngreso/RegistroIngreso";
import ScanDNIView from "./ScanDNI/ScanDNI";
import { useStyles } from "./LogisticParkDashboard.styles";
import { Box } from "@material-ui/core";
import HeaderMobile from "../../components/common/PL/Header/HeaderMobile";
import { useRegistroIngresoSession } from "./RegistroIngreso/session/RegistroIngresoSessionContext";
import FormTabBar from "../../components/common/PL/Tabs/FormTabBar/FormTabBar";
import { useHistory, useLocation } from "react-router-dom";
import { ScanDniNavigationState } from "./ScanDNI/scanDniNavigation";
import DialogConfirmation from "../../components/common/PL/Dialog/DialogConfirmation";

const LogisticParkDashboard = () => {
  const classes = useStyles();
  const { authStore } = useStore();
  const history = useHistory();
  const location = useLocation();

  const { tabs, activeTabId, setActiveTab, closeTab, clearSession } = useRegistroIngresoSession();
  const [tabToClose, setTabToClose] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (tabs.length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [tabs.length]);

  const handleAddTab = () => {
    const activeTab = tabs.find((t) => t.id === activeTabId);
    const currentMode = activeTab?.mode || "entry";
    const state: ScanDniNavigationState = {
      mode: currentMode,
      intent: "append-tab",
    };
    history.push({
      pathname: RoutePaths.LogisticParkScanDNI,
      state,
    });
  };

  const isRegistrationRoute = location.pathname === RoutePaths.LogisticParkEntryRegistration;

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (!isRegistrationRoute) {
      history.push(RoutePaths.LogisticParkEntryRegistration);
    }
  };

  const requestCloseTab = (tabId: string) => {
    setTabToClose(tabId);
  };

  const confirmCloseTab = () => {
    if (tabToClose) {
      if (tabs.length > 1) {
        closeTab(tabToClose);
      } else {
        clearSession();
        history.push(RoutePaths.LogisticParkHome);
      }
      setTabToClose(null);
    }
  };

  const cancelCloseTab = () => {
    setTabToClose(null);
  };

  const tabsForBar = tabs.map((tab, index) => {
    const modePrefix = tab.mode === "exit" ? "Salida" : "Entrada";
    const firstName = tab.values.firstName || "";
    const lastName = tab.values.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();

    const displayName = fullName || String(index + 1);
    return {
      id: tab.id,
      label: `${modePrefix} - ${displayName}`,
    };
  });

  const activeBuilding = authStore.country?.countryName || "Ubicación Desconocida";

  const userName = authStore.user?.name || "Usuario";

  return (
    <div className={classes.root}>
      {/* PL Sidebar */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Sidebar />
      </Box>

      <div className={classes.content}>
        {/* PL Header */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Header title={activeBuilding} location={activeBuilding} name={userName} />
        </Box>
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <HeaderMobile />
        </Box>

        {/* Main Content Area */}
        <div className={classes.mainArea}>
          {tabsForBar.length > 0 && (
            <Box mb={2}>
              <FormTabBar
                tabs={tabsForBar}
                activeTabId={isRegistrationRoute ? activeTabId || tabsForBar[0]?.id || "" : ""}
                onTabChange={handleTabChange}
                onTabAdd={handleAddTab}
                onTabClose={requestCloseTab}
              />
            </Box>
          )}
          <Switch>
            <ProtectedRoutes
              path={RoutePaths.LogisticParkEntryRegistration}
              component={RegistroIngreso}
            />
            <ProtectedRoutes path={RoutePaths.LogisticParkHome} exact component={Home} />
            <ProtectedRoutes path={RoutePaths.LogisticParkScanDNI} exact component={ScanDNIView} />
            <ProtectedRoutes path={RoutePaths.LogisticParkExitUsers} component={ExitUsers} />
            {/* Any future LP routes will go here */}
            <ProtectedRoutes path={RoutePaths.LogisticPark} exact component={Home} />
          </Switch>
        </div>
      </div>
      <DialogConfirmation
        open={!!tabToClose}
        onClose={cancelCloseTab}
        onConfirm={confirmCloseTab}
        title="Cerrar Formulario"
        message="¿Está seguro que desea cerrar esta pestaña? Se perderán todos los datos ingresados."
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
    </div>
  );
};

export default LogisticParkDashboard;
